import { useEffect, useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import slide1 from "../../../assets/slide.jpg";
import slide2 from "../../../assets/slide2.jpg";
import slide3 from "../../../assets/slide3.jpg";

const slides = [
    {
        id: 1,
        title: "Tìm Kiếm Ngôi Nhà Mơ Ước",
        subtitle: "Hàng nghìn bất động sản đang chờ bạn",
        description: "Khám phá danh sách nhà đất, căn hộ, biệt thự được cập nhật mới nhất, minh bạch thông tin.",
        image_url: slide1,
    },
    {
        id: 2,
        title: "Đầu Tư Thông Minh, Sinh Lời Vượt Trội",
        subtitle: "Dữ liệu thị trường & Tư vấn chuyên sâu",
        description: "Tiếp cận cơ hội đầu tư hấp dẫn với phân tích chuyên gia và báo cáo thị trường độc quyền.",
        image_url: slide2,
    },
    {
        id: 3,
        title: "Giao Dịch An Toàn & Hiệu Quả",
        subtitle: "Đội ngũ môi giới uy tín, giàu kinh nghiệm",
        description: "Chúng tôi đồng hành cùng bạn từ tìm kiếm, định giá đến hoàn tất thủ tục pháp lý.",
        image_url: slide3,
    },
];

const SLIDE_DURATION = 6000;
const TRANSITION_DURATION = 1000;

const textVariants = {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: -20, opacity: 0, transition: { duration: 0.3 } },
};

export default function Hero() {
    const navigate = useNavigate();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [keyword, setKeyword] = useState("");
    const [propertyType, setPropertyType] = useState("");

    const images = useMemo(() => slides, []);

    useEffect(() => {
        const loadTimeout = setTimeout(() => setLoading(false), 800);
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % images.length);
        }, SLIDE_DURATION);

        return () => {
            clearTimeout(loadTimeout);
            clearInterval(interval);
        };
    }, [images.length]);

    const goToSlide = useCallback((index) => setCurrentIndex(index), []);

    // 🔹 Hàm tìm kiếm
    const handleSearch = (e) => {
        e.preventDefault();
        const query = {};
        if (keyword) query.keyword = keyword;
        if (propertyType) query.propertyType = propertyType;

        navigate({
            pathname: "/search",
            search: new URLSearchParams(query).toString(),
        });
    };


    if (loading) {
        return (
            <section className="w-full min-h-screen flex items-center justify-center text-white bg-gray-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F7C948]"></div>
            </section>
        );
    }

    return (
        <section className="min-h-screen pt-[80px] flex flex-col items-center justify-center text-white px-4 text-center relative overflow-hidden">
            {/* Background slider */}
            <div className="absolute inset-0 w-full h-full overflow-hidden">
                <AnimatePresence>
                    {images.map(
                        (image, index) =>
                            index === currentIndex && (
                                <motion.div
                                    key={image.id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: TRANSITION_DURATION / 1000 }}
                                    className="absolute inset-0 w-full h-full"
                                >
                                    <motion.img
                                        src={image.image_url}
                                        alt={image.title}
                                        loading="lazy"
                                        initial={{ scale: 1.05 }}
                                        animate={{ scale: 1 }}
                                        transition={{ duration: SLIDE_DURATION / 1000 + 1, ease: "linear" }}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
                                </motion.div>
                            )
                    )}
                </AnimatePresence>
            </div>

            {/* Content (Text & Title) */}
            <div className="z-20 p-4 md:p-8 max-w-6xl w-full mx-auto font-serif">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={images[currentIndex].id + "-content"}
                        className="flex flex-col items-center"
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        variants={{ animate: { transition: { staggerChildren: 0.1 } } }}
                    >
                        <motion.p
                            key={images[currentIndex].id + "-subtitle"}
                            variants={textVariants}
                            className="text-lg md:text-2xl font-semibold uppercase tracking-widest text-[#F7C948] drop-shadow-[0_4px_8px_rgba(0,0,0,0.4)] mb-4 font-serif"
                        >
                            {images[currentIndex].subtitle}
                        </motion.p>
                        <motion.h1
                            key={images[currentIndex].id + "-title"}
                            variants={textVariants}
                            className="text-3xl md:text-8xl font-extrabold mb-6 font-['Playfair Display'] drop-shadow-xl"
                        >
                            {images[currentIndex].title}
                        </motion.h1>
                        <motion.p
                            key={images[currentIndex].id + "-desc"}
                            variants={textVariants}
                            className="text-lg md:text-2xl mb-12 max-w-3xl mx-auto text-gray-200 font-light leading-relaxed"
                        >
                            {images[currentIndex].description}
                        </motion.p>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* ✅ SearchBar (JSX được đặt trực tiếp vào đây để tránh re-render không cần thiết) */}
            <div className="w-full max-w-4xl mx-auto mb-24 md:mb-28 z-20 relative">
                <div className="bg-white/95 backdrop-blur-sm p-3 md:p-4 rounded-xl shadow-2xl border border-amber-300/30">
                    <form className="flex flex-col md:flex-row gap-3" onSubmit={handleSearch}>
                        <input
                            type="text"
                            placeholder="Nhập tên đường, khu vực hoặc dự án..."
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            className="flex-grow p-3 rounded-lg border border-gray-300 focus:outline-none focus:border-amber-500 text-gray-800 text-base"
                        />
                        <select
                            value={propertyType}
                            onChange={(e) => setPropertyType(e.target.value)}
                            className="p-3 rounded-lg border border-gray-300 focus:outline-none text-gray-800 text-base md:w-40"
                        >
                            <option value="">Loại BĐS</option>
                            <option value="apartment">Căn hộ</option>
                            <option value="house">Nhà riêng</option>
                            <option value="land">Đất nền</option>
                        </select>
                        <button
                            type="submit"
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition duration-200"
                        >
                            <FaSearch />
                            Tìm Kiếm
                        </button>
                    </form>
                </div>
            </div>

            {/* Slider dots */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex space-x-3">
                {images.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`h-3 rounded-full transition-all ${index === currentIndex ? "bg-[#F7C948] w-8" : "bg-white/60 w-3"}`}
                    />
                ))}
            </div>
        </section>
    );
}